(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;

  function decodeHtml(value) {
    if (!value) {
      return '';
    }

    const textarea = root.document.createElement('textarea');
    textarea.innerHTML = String(value);
    return textarea.value;
  }

  function computeAverageScore(reviews) {
    if (!Array.isArray(reviews) || !reviews.length) {
      return 0;
    }

    const total = reviews.reduce((sum, review) => sum + (Number(review.score) || 0), 0);
    return total / reviews.length;
  }

  function buildDistribution(reviews) {
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    if (!Array.isArray(reviews)) {
      return distribution;
    }

    reviews.forEach((review) => {
      const score = Math.round(Number(review?.score) || 0);

      if (distribution[score] !== undefined) {
        distribution[score] += 1;
      }
    });

    return distribution;
  }

  function parseStarDistribution(starDistribution) {
    if (!starDistribution || typeof starDistribution !== 'object') {
      return null;
    }

    const parsed = {
      1: Number(starDistribution['1']) || 0,
      2: Number(starDistribution['2']) || 0,
      3: Number(starDistribution['3']) || 0,
      4: Number(starDistribution['4']) || 0,
      5: Number(starDistribution['5']) || 0,
    };

    const total = [1, 2, 3, 4, 5].reduce((sum, rating) => sum + parsed[rating], 0);
    return total > 0 ? parsed : null;
  }

  function estimateDistributionPercents(averageScore) {
    const average = Number(averageScore) || 0;

    if (average <= 0) {
      return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    }

    if (average >= 5) {
      return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 100 };
    }

    if (average <= 1) {
      return { 1: 100, 2: 0, 3: 0, 4: 0, 5: 0 };
    }

    const high = Math.ceil(average);
    const low = Math.floor(average);
    const highPercent = Math.round((average - low) * 100);
    const lowPercent = 100 - highPercent;
    const percents = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    if (high === low) {
      percents[high] = 100;
      return percents;
    }

    percents[high] = highPercent;
    percents[low] = lowPercent;
    return percents;
  }

  function resolveDistributionState({
    starDistribution,
    averageScore,
    reviews,
    fallbackPercents = {},
  } = {}) {
    const parsed = parseStarDistribution(starDistribution);

    if (parsed) {
      return {
        distribution: parsed,
        fallbackPercents,
      };
    }

    const average = Number(averageScore) || 0;
    if (average > 0) {
      // Widget review pages can omit lower scores while bottomline.average_score includes them.
      // Prefer an average-based estimate over a biased newest-page sample.
      return {
        distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        fallbackPercents: estimateDistributionPercents(average),
      };
    }

    const fromReviews = buildDistribution(reviews);
    const reviewTotal = [1, 2, 3, 4, 5].reduce((sum, rating) => sum + fromReviews[rating], 0);

    if (reviewTotal > 0) {
      return {
        distribution: fromReviews,
        fallbackPercents,
      };
    }

    return {
      distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      fallbackPercents,
    };
  }

  function buildDistributionRows(distribution, fallbackPercents = {}) {
    const distributionTotal = [1, 2, 3, 4, 5].reduce(
      (sum, rating) => sum + (Number(distribution?.[rating]) || 0),
      0
    );

    if (distributionTotal > 0) {
      return [5, 4, 3, 2, 1].map((rating) => ({
        rating,
        percent: Math.round(((Number(distribution[rating]) || 0) / distributionTotal) * 100),
      }));
    }

    return [5, 4, 3, 2, 1].map((rating) => ({
      rating,
      percent: Number(fallbackPercents[rating]) || 0,
    }));
  }

  function buildKalturaVideoUrl(thumbnailUrl, entryId) {
    if (!entryId) {
      return '';
    }

    const match = String(thumbnailUrl || '').match(/\/p\/(\d+)\/sp\/(\d+)\//);
    if (!match) {
      return '';
    }

    const partnerId = match[1];
    const subPartnerId = match[2];

    return `https://cfvod.kaltura.com/p/${partnerId}/sp/${subPartnerId}/playManifest/entryId/${encodeURIComponent(entryId)}/format/url/protocol/https/flavorParamId/0/video.mp4`;
  }

  function reviewAltText(review) {
    const title = review?.title || '';
    const content = review?.content || '';

    if (title) {
      return title.slice(0, 120);
    }

    if (content) {
      return content.slice(0, 120);
    }

    return 'Customer review';
  }

  function extractVideoTiles(reviews, limit) {
    const tiles = [];

    if (!Array.isArray(reviews) || !limit) {
      return tiles;
    }

    for (const review of reviews) {
      const videos = Array.isArray(review?.videos_data) ? review.videos_data : [];

      for (const video of videos) {
        const entryId = video?.entry_id || '';
        const posterUrl = video?.thumbnail_url || '';

        if (!entryId || !posterUrl) {
          continue;
        }

        tiles.push({
          id: `video-${review?.id || 'review'}-${video?.id || entryId}`,
          reviewId: review?.id || '',
          videoUrl: buildKalturaVideoUrl(posterUrl, entryId),
          posterUrl,
          alt: reviewAltText(review),
        });

        if (tiles.length >= limit) {
          return tiles;
        }
      }
    }

    return tiles;
  }

  function extractImageTiles(reviews, limit, usedReviewIds = new Set()) {
    const tiles = [];

    if (!Array.isArray(reviews) || !limit) {
      return tiles;
    }

    for (const review of reviews) {
      if (usedReviewIds.has(review?.id)) {
        continue;
      }

      const images = Array.isArray(review?.images_data) ? review.images_data : [];

      for (const image of images) {
        const imageUrl = image?.original_url || image?.thumb_url || '';

        if (!imageUrl) {
          continue;
        }

        tiles.push({
          id: `image-${review?.id || 'review'}-${image?.id || imageUrl}`,
          reviewId: review?.id || '',
          imageUrl,
          alt: reviewAltText(review),
        });

        if (tiles.length >= limit) {
          return tiles;
        }
      }
    }

    return tiles;
  }

  function buildGalleryTiles(reviews, limit, fallbackTiles) {
    const videoTiles = extractVideoTiles(reviews, limit);
    const usedReviewIds = new Set(
      videoTiles.map((tile) => tile.reviewId).filter(Boolean)
    );
    const imageLimit = Math.max(limit - videoTiles.length, 0);
    const imageTiles = imageLimit > 0
      ? extractImageTiles(reviews, imageLimit, usedReviewIds)
      : [];
    const merged = [...videoTiles, ...imageTiles];
    let fallbackIndex = 0;

    while (merged.length < limit && fallbackIndex < fallbackTiles.length) {
      merged.push(fallbackTiles[fallbackIndex]);
      fallbackIndex += 1;
    }

    return merged;
  }

  const reviewCache = new Map();
  const reviewPageCache = new Map();

  async function fetchReviewsPage(appKey, options = {}) {
    const page = Math.max(1, Number(options.page) || 1);
    const perPage = Number(options.perPage) || 9;
    const cacheKey = `${appKey}:${perPage}:${page}`;

    if (reviewPageCache.has(cacheKey)) {
      return reviewPageCache.get(cacheKey);
    }

    const request = (async () => {
      const response = await fetch(
        `https://api.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/reviews.json?per_page=${perPage}&page=${page}`
      );

      if (!response.ok) {
        throw new Error(`Yotpo request failed: ${response.status}`);
      }

      const data = await response.json();
      const responseBody = data?.response || {};

      return {
        reviews: Array.isArray(responseBody.reviews) ? responseBody.reviews : [],
        bottomline: responseBody.bottomline || {},
        pagination: responseBody.pagination || {},
        // Reviews carry only a Yotpo product_id; the names live in this map.
        products: Array.isArray(responseBody.products) ? responseBody.products : [],
      };
    })().catch((error) => {
      reviewPageCache.delete(cacheKey);
      throw error;
    });

    reviewPageCache.set(cacheKey, request);
    return request;
  }

  async function fetchAllReviews(appKey, options = {}) {
    const maxPages = Number(options.maxPages) || 10;
    const perPage = Number(options.perPage) || 50;
    const cacheKey = `${appKey}:${maxPages}:${perPage}`;

    if (reviewCache.has(cacheKey)) {
      return reviewCache.get(cacheKey);
    }

    const request = (async () => {
      const allReviews = [];
      let bottomline = {};
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages && page <= maxPages) {
        const response = await fetch(
          `https://api.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/reviews.json?per_page=${perPage}&page=${page}`
        );

        if (!response.ok) {
          throw new Error(`Yotpo request failed: ${response.status}`);
        }

        const data = await response.json();
        const responseBody = data?.response || {};
        const reviews = Array.isArray(responseBody.reviews) ? responseBody.reviews : [];
        const pagination = responseBody.pagination || {};
        const total = Number(pagination.total) || reviews.length;
        const pageSize = Number(pagination.per_page) || perPage;

        if (page === 1) {
          bottomline = responseBody.bottomline || {};
        }

        allReviews.push(...reviews);
        totalPages = Math.max(1, Math.ceil(total / pageSize));
        page += 1;
      }

      return { reviews: allReviews, bottomline };
    })();

    reviewCache.set(cacheKey, request);
    return request;
  }

  // Yotpo's site reviews point at a placeholder product standing in for the store
  // itself. Depending on the account that placeholder is named "yotpo_site_reviews",
  // "Store review", or -- as here -- the myshopify domain, so a bare list of names
  // is not enough: anything shaped like a hostname is rejected too. Labelling
  // store feedback "on domestic-betta.myshopify.com" is worse than no label.
  const SITE_REVIEW_PRODUCT_NAMES = new Set(['yotpo_site_reviews', 'Store review', 'Site Reviews']);
  const HOSTNAME_LIKE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i;

  function buildProductNameMap(products) {
    const map = new Map();

    (Array.isArray(products) ? products : []).forEach((product) => {
      const name = String(product?.name || '').trim();
      if (!product?.id || !name) {
        return;
      }
      if (SITE_REVIEW_PRODUCT_NAMES.has(name) || HOSTNAME_LIKE.test(name)) {
        return;
      }
      map.set(String(product.id), name);
    });

    return map;
  }

  function normalizeTestimonialReview(review, locale, productNames) {
    const displayName = review?.user?.display_name
      || review?.user?.displayName
      || review?.reviewer_display_name
      || review?.display_name
      || review?.name
      || 'Customer';
    const initial = String(displayName).trim().charAt(0).toUpperCase() || 'C';
    const createdAt = review?.created_at || review?.createdAt;

    // Yotpo returns attached photos as images_data; each entry carries a square
    // thumbnail and the original. Videos are not exposed on this endpoint, so a
    // review with only a video normalises to no media rather than a broken tile.
    const images = Array.isArray(review?.images_data)
      ? review.images_data
        .map((image) => ({
          id: image?.id || '',
          thumb: image?.thumb_url || image?.original_url || '',
          full: image?.original_url || image?.thumb_url || '',
        }))
        .filter((image) => image.thumb)
      : [];

    return {
      id: review?.id || '',
      score: Number(review?.score) || 0,
      title: decodeHtml(review?.title || ''),
      content: decodeHtml(review?.content || ''),
      name: displayName,
      initial,
      date: formatRelativeReviewDate(createdAt, locale),
      createdAt: createdAt || '',
      images,
      hasMedia: images.length > 0,
      verifiedBuyer: Boolean(review?.verified_buyer),
      productName: productNames instanceof Map
        ? (productNames.get(String(review?.product_id)) || '')
        : '',
    };
  }

  function formatRelativeReviewDate(isoDate, locale) {
    if (!isoDate) {
      return '';
    }

    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return new Intl.DateTimeFormat(locale || 'en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
    }

    if (diffDays === 1) {
      return '1 day ago';
    }

    if (diffDays < 30) {
      return `${diffDays} days ago`;
    }

    return new Intl.DateTimeFormat(locale || 'en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  async function fetchSiteReviewsPage(appKey, options = {}) {
    const page = Math.max(1, Number(options.page) || 1);
    const perPage = Number(options.perPage) || 9;
    const cacheKey = `site:${appKey}:${perPage}:${page}`;

    if (reviewPageCache.has(cacheKey)) {
      return reviewPageCache.get(cacheKey);
    }

    const request = (async () => {
      const response = await fetch(
        `https://api.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/yotpo_site_reviews/reviews.json?per_page=${perPage}&page=${page}`
      );

      if (!response.ok) {
        throw new Error(`Yotpo site reviews request failed: ${response.status}`);
      }

      const data = await response.json();
      const responseBody = data?.response || {};

      return {
        reviews: Array.isArray(responseBody.reviews) ? responseBody.reviews : [],
        bottomline: responseBody.bottomline || {},
        pagination: responseBody.pagination || {},
        // Reviews carry only a Yotpo product_id; the names live in this map.
        products: Array.isArray(responseBody.products) ? responseBody.products : [],
      };
    })().catch((error) => {
      reviewPageCache.delete(cacheKey);
      throw error;
    });

    reviewPageCache.set(cacheKey, request);
    return request;
  }

  let oauthTokenPromise = null;
  let oauthTokenCacheKey = '';

  async function requestOAuthToken(appKey, secret) {
    const jsonAttempt = await fetch('https://api.yotpo.com/oauth/token', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: appKey,
        client_secret: secret,
        grant_type: 'client_credentials',
      }),
    });

    if (jsonAttempt.ok) {
      const data = await jsonAttempt.json();
      const token = data?.access_token || data?.utoken || '';
      if (token) {
        return token;
      }
    }

    // Some environments accept form-encoded OAuth better than JSON.
    const formBody = new URLSearchParams({
      client_id: appKey,
      client_secret: secret,
      grant_type: 'client_credentials',
    });

    const formAttempt = await fetch('https://api.yotpo.com/oauth/token', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formBody.toString(),
    });

    if (!formAttempt.ok) {
      throw new Error(`Yotpo OAuth failed: ${formAttempt.status || jsonAttempt.status}`);
    }

    const formData = await formAttempt.json();
    const formToken = formData?.access_token || formData?.utoken || '';

    if (!formToken) {
      throw new Error('Yotpo OAuth token missing');
    }

    return formToken;
  }

  async function getOAuthToken(appKey, secret) {
    const cacheKey = `${appKey}:${secret}`;

    if (oauthTokenPromise && oauthTokenCacheKey === cacheKey) {
      return oauthTokenPromise;
    }

    oauthTokenCacheKey = cacheKey;
    oauthTokenPromise = requestOAuthToken(appKey, secret).catch((error) => {
      oauthTokenPromise = null;
      oauthTokenCacheKey = '';
      throw error;
    });

    return oauthTokenPromise;
  }

  function sortReviewsNewestFirst(reviews) {
    return [...(reviews || [])].sort((left, right) => {
      const leftTime = new Date(left?.created_at || left?.createdAt || 0).getTime();
      const rightTime = new Date(right?.created_at || right?.createdAt || 0).getTime();
      return rightTime - leftTime;
    });
  }

  function resolveAccountReviewTotal(payload = {}) {
    const responseBody = payload?.response || payload || {};
    const pagination = responseBody.pagination || payload.pagination || {};

    return Number(
      pagination.total
      ?? responseBody.total_reviews
      ?? responseBody.totalReviews
      ?? responseBody.total
      ?? payload.total_reviews
      ?? payload.totalReviews
      ?? payload.total
    ) || 0;
  }

  function parseAccountReviewsPayload(data) {
    const responseBody = data?.response || data || {};
    const reviews = Array.isArray(responseBody.reviews) ? responseBody.reviews : [];
    const pagination = responseBody.pagination || {};
    const total = resolveAccountReviewTotal(data);

    // Keep only published reviews when the admin API marks deleted ones.
    const published = reviews.filter((review) => {
      if (review?.deleted === true || review?.deleted === 'true') {
        return false;
      }

      if (review?.published === false || review?.published === 'false') {
        return false;
      }

      const status = String(review?.status || review?.review_status || '').toLowerCase();
      if (status && (status === 'deleted' || status === 'unpublished' || status === 'rejected')) {
        return false;
      }

      return true;
    });

    return {
      reviews: sortReviewsNewestFirst(published),
      pagination,
      total,
      responseBody,
    };
  }

  const accountReviewTotalCache = new Map();

  /**
   * Published review total from Admin Reviews API when no stats endpoint exists.
   * Paginate count=100 until a page returns fewer reviews than count, then sum lengths.
   */
  async function fetchAccountReviewTotal(appKey, secret) {
    const cacheKey = String(appKey || '');
    if (!cacheKey || !secret) {
      return 0;
    }

    if (accountReviewTotalCache.has(cacheKey)) {
      return accountReviewTotalCache.get(cacheKey);
    }

    const request = (async () => {
      const pageSize = 100;
      const maxPages = 100;
      let page = 1;
      let total = 0;

      while (page <= maxPages) {
        const { reviews } = await fetchAccountReviewsPage(appKey, secret, {
          page,
          count: pageSize,
        });
        const pageCount = Array.isArray(reviews) ? reviews.length : 0;
        total += pageCount;

        if (pageCount < pageSize) {
          break;
        }

        page += 1;
      }

      return total;
    })().catch((error) => {
      accountReviewTotalCache.delete(cacheKey);
      throw error;
    });

    accountReviewTotalCache.set(cacheKey, request);
    return request;
  }

  async function fetchAccountReviewsPage(appKey, secret, options = {}) {
    const count = Math.min(100, Math.max(1, Number(options.count) || 20));
    const page = Math.max(1, Number(options.page) || 1);
    const utoken = await getOAuthToken(appKey, secret);
    // Legacy utoken reviews endpoint has no reliable newest-first sort.
    // Default order is oldest-first by id; callers must request the last page for newest.
    const params = new URLSearchParams({
      utoken,
      count: String(count),
      page: String(page),
      deleted: 'false',
    });

    if (options.includeSiteReviews !== false) {
      params.set('include_site_reviews', 'true');
    }

    // Best-effort sort hints for newer API gateways (ignored by many legacy accounts).
    params.set('sort', 'date');
    params.set('sortby', 'date');
    params.set('direction', options.direction || 'desc');

    const response = await fetch(
      `https://api.yotpo.com/v1/apps/${encodeURIComponent(appKey)}/reviews?${params.toString()}`
    );

    if (!response.ok) {
      throw new Error(`Yotpo account reviews failed: ${response.status}`);
    }

    const data = await response.json();
    const parsed = parseAccountReviewsPayload(data);

    // If the gateway ignored sort and returned oldest-first, optionally reverse.
    if (options.forceNewestFirst) {
      parsed.reviews = sortReviewsNewestFirst(parsed.reviews);
    }

    return parsed;
  }

  async function fetchNewestAccountReviews(appKey, secret, options = {}) {
    const count = Math.min(100, Math.max(1, Number(options.count) || 50));
    const total = await fetchAccountReviewTotal(appKey, secret);

    if (!total) {
      return { reviews: [], total: 0, pagination: {} };
    }

    const totalPages = Math.max(1, Math.ceil(total / count));
    // Legacy Admin API pages oldest-first: last page holds the newest reviews.
    const newestPage = await fetchAccountReviewsPage(appKey, secret, {
      page: totalPages,
      count,
      direction: 'desc',
      forceNewestFirst: true,
    });

    return {
      reviews: sortReviewsNewestFirst(newestPage.reviews),
      total,
      pagination: newestPage.pagination,
    };
  }

  async function fetchRecentAccountReviews(appKey, secret, options = {}) {
    return fetchAccountReviewsPage(appKey, secret, {
      page: 1,
      count: options.count || 20,
    });
  }

  function clearReviewCaches() {
    reviewPageCache.clear();
    reviewCache.clear();
    accountReviewTotalCache.clear();
  }

  function buildCreateReviewFields(appKey, payload = {}) {
    return {
      appkey: appKey,
      domain: payload.domain || payload.shop_domain || '',
      sku: payload.sku || payload.product_id || 'yotpo_site_reviews',
      product_title: payload.productTitle || payload.product_title || 'Store review',
      product_url: payload.productUrl || payload.product_url || '',
      product_description: payload.productDescription || payload.product_description || '',
      product_image_url: payload.productImageUrl || payload.product_image_url || '',
      display_name: payload.displayName || payload.display_name || payload.user_display_name || '',
      email: payload.email || payload.user_email || '',
      review_content: payload.reviewContent || payload.review_content || payload.review_body || '',
      review_title: payload.reviewTitle || payload.review_title || '',
      review_score: Number(payload.reviewScore || payload.review_score) || 0,
    };
  }

  function extractCreateReviewError(data, status) {
    const statusPayload = data?.status || {};
    const parts = [
      statusPayload.error_type,
      statusPayload.message,
      data?.error_type,
      data?.message,
      data?.error,
    ].filter(Boolean);

    if (parts.length) {
      return [...new Set(parts.map(String))].join(': ');
    }

    return `Yotpo create review failed: ${status}`;
  }

  function isCreateReviewSuccess(response, data) {
    const code = Number(data?.code ?? data?.status?.code ?? response.status);
    return response.ok && (Number.isNaN(code) || code === 200);
  }

  async function parseJsonResponse(response) {
    try {
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Documented widget create endpoint (preferred):
   * POST /v1/widget/{app_key}/reviews
   */
  async function createReviewViaWidgetPath(appKey, payload = {}) {
    const fields = buildCreateReviewFields(appKey, payload);
    const response = await fetch(
      `https://api.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/reviews`,
      {
        method: 'POST',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fields),
      }
    );

    const data = await parseJsonResponse(response);
    if (!isCreateReviewSuccess(response, data)) {
      throw new Error(extractCreateReviewError(data, response.status));
    }

    return data;
  }

  /**
   * Legacy form endpoint still used by many storefront widgets:
   * POST /v1/widget/reviews
   */
  async function createReviewViaWidgetForm(appKey, payload = {}) {
    const fields = buildCreateReviewFields(appKey, payload);
    const body = new URLSearchParams(
      Object.fromEntries(
        Object.entries(fields).map(([key, value]) => [key, String(value ?? '')])
      )
    );

    const response = await fetch('https://api.yotpo.com/v1/widget/reviews', {
      method: 'POST',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    const data = await parseJsonResponse(response);
    if (!isCreateReviewSuccess(response, data)) {
      throw new Error(extractCreateReviewError(data, response.status));
    }

    return data;
  }

  /**
   * Classic Yotpo SDK endpoint (GET can be cached by browsers/proxies — avoid as primary).
   * GET /v1/reviews/dynamic_create
   */
  async function createReviewViaDynamicCreate(appKey, payload = {}) {
    const fields = buildCreateReviewFields(appKey, payload);
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries(fields).map(([key, value]) => [key, String(value ?? '')])
      )
    );
    params.set('_ts', String(Date.now()));

    const response = await fetch(
      `https://api.yotpo.com/v1/reviews/dynamic_create?${params.toString()}`,
      {
        method: 'GET',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
        },
      }
    );

    const data = await parseJsonResponse(response);
    if (!isCreateReviewSuccess(response, data)) {
      throw new Error(extractCreateReviewError(data, response.status));
    }

    return data;
  }

  async function createReview(appKey, payload = {}) {
    const attempts = [
      () => createReviewViaWidgetPath(appKey, payload),
      () => createReviewViaWidgetForm(appKey, payload),
      () => createReviewViaDynamicCreate(appKey, payload),
    ];

    let lastError = null;

    for (const attempt of attempts) {
      try {
        const data = await attempt();
        clearReviewCaches();
        return data;
      } catch (error) {
        lastError = error;
        const message = String(error?.message || '');
        // Stop retrying when Yotpo says this is a duplicate — other endpoints will reject too.
        if (/duplicate/i.test(message)) {
          throw error;
        }
      }
    }

    throw lastError || new Error('Yotpo create review failed');
  }

  root.JFTYotpo = {
    decodeHtml,
    computeAverageScore,
    buildDistribution,
    parseStarDistribution,
    estimateDistributionPercents,
    resolveDistributionState,
    buildDistributionRows,
    buildKalturaVideoUrl,
    reviewAltText,
    extractVideoTiles,
    extractImageTiles,
    buildGalleryTiles,
    fetchAllReviews,
    fetchReviewsPage,
    fetchSiteReviewsPage,
    getOAuthToken,
    resolveAccountReviewTotal,
    fetchAccountReviewTotal,
    fetchAccountReviewsPage,
    fetchNewestAccountReviews,
    fetchRecentAccountReviews,
    clearReviewCaches,
    createReview,
    buildProductNameMap,
    normalizeTestimonialReview,
    formatRelativeReviewDate,
  };
})();
